import type {
  AllowanceBasis,
  CoverKind,
  FillBasis,
  LoadBasis,
  RatedLoadBasis,
  TrayArrangement,
  TrayType,
} from "../services/cableTrayContract";
import type { ValidationLabels } from "../utils/validationMessages";

// The words of the cable tray study (EOS-12, Master Prompt A34): the form's labels, keyed by
// the request path with list indexes as "*", and the label maps of the controlled vocabularies
// the panels print. Exhaustive maps: a new member without a label is a compile error.

export const TRAY_TYPE_LABELS: Record<TrayType, string> = {
  CABLE_TRAY_PERFORATED: "Perforated cable tray",
  CABLE_TRAY_SOLID: "Solid-bottom cable tray",
  CABLE_LADDER: "Cable ladder",
  WIRE_MESH: "Wire-mesh tray",
};

export const ARRANGEMENT_LABELS: Record<TrayArrangement, string> = {
  SINGLE_LAYER_TOUCHING: "Single layer, touching",
  SINGLE_LAYER_SPACED: "Single layer, spaced",
  TREFOIL_TOUCHING: "Trefoil, touching",
  TREFOIL_SPACED: "Trefoil, spaced",
  MULTI_LAYER: "Multi-layer (bunched)",
};

export const COVER_LABELS: Record<CoverKind, string> = {
  NONE: "No cover",
  VENTILATED: "Ventilated cover",
  SOLID: "Solid cover",
};

export const RATED_LOAD_BASIS_LABELS: Record<RatedLoadBasis, string> = {
  PER_METRE: "per metre",
  PER_SPAN: "per span",
};

export const ALLOWANCE_BASIS_LABELS: Record<AllowanceBasis, string> = {
  AREA_AND_LOAD: "occupied area and load",
  WIDTH_AND_LOAD: "occupied area, single-layer width and load",
};

export const FILL_BASIS_LABELS: Record<FillBasis, string> = {
  DECLARED_LIMIT: "Declared fill limit",
  USABLE_WIDTH: "Usable width (single layer)",
  NOT_ESTABLISHED: "Fill stated, no limit declared",
  NOT_CHECKED: "Not checked (a diameter is missing)",
};

export const LOAD_BASIS_LABELS: Record<LoadBasis, string> = {
  DECLARED_RATING: "Declared rated load at its span",
  SPAN_NOT_APPLICABLE: "Rating not applicable (support spacing beyond the rated span)",
  NOT_CHECKED: "Not checked (an input is missing)",
};

export const CABLE_TRAY_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Route code",
    "study.name": "Route name",
    "study.tray_type": "Tray type",
    "study.arrangement": "Cable arrangement",
    "study.usable_width_mm": "Usable width (mm)",
    "study.usable_depth_mm": "Usable depth (mm)",
    "study.dimensions_source": "Dimensions source",
    "study.cover": "Cover",
    "study.fill_limit": "Fill limit",
    "study.fill_limit.percent": "Fill limit (%)",
    "study.fill_limit.source": "Fill limit source",
    "study.rated_load": "Rated load",
    "study.rated_load.value_kg": "Rated load (kg)",
    "study.rated_load.basis": "Rated load basis",
    "study.rated_load.rated_span_m": "Rated span (m)",
    "study.rated_load.source": "Rated load source",
    "study.support_spacing": "Support spacing",
    "study.support_spacing.value_m": "Support spacing (m)",
    "study.support_spacing.source": "Support spacing source",
    "study.future_allowance": "Future allowance",
    "study.future_allowance.percent": "Future allowance (%)",
    "study.future_allowance.source": "Future allowance source",
    "study.future_allowance.basis": "Future allowance applies to",
    "study.rows": "Cable rows",
    "study.rows.*.code": "Row code",
    "study.rows.*.name": "Row name",
    "study.rows.*.cable_run_id": "Cable run",
    "study.rows.*.cable": "Typed cable",
    "study.rows.*.cable.construction": "Construction",
    "study.rows.*.cable.cores": "Cores",
    "study.rows.*.cable.conductor_material": "Conductor material",
    "study.rows.*.cable.insulation_material": "Insulation",
    "study.rows.*.cable.source": "Cable source",
    "study.rows.*.quantity": "Number of cables",
    "study.rows.*.quantity_source": "Quantity source",
    "study.rows.*.overall_diameter_mm": "Overall diameter D (mm)",
    "study.rows.*.diameter_source": "Diameter source",
    "study.rows.*.mass_kg_per_m": "Mass per metre (kg/m)",
    "study.rows.*.mass_source": "Mass source",
    "study.rows.*.spacing_mm": "Clear spacing (mm)",
    "study.rows.*.spacing_in_diameters": "Clear spacing (diameters)",
    "study.rows.*.spacing_source": "Spacing source",
    "study.rows.*.sld_element_code": "Feeder (SLD)",
    "study.sld_element_codes": "Feeders carried (SLD)",
    "study.jurisdiction_profile": "Jurisdiction profile",
    "study.notes": "Notes",
    project_revision_id: "Project revision",
    notes: "Run notes",
  },
  groups: {
    rows: "Row",
  },
};
